import React from 'react'

import { FormProvider, useForm } from 'react-hook-form'
import { GoPay } from '@sga/shopify-payments-types'

import {
  Banner,
  Button,
  Card,
  Form,
  FormLayout,
  Heading,
  Stack,
  TextContainer,
  TextStyle,
} from '@shopify/polaris'

import { createApi } from '../api'
import { segment } from '../util/createSegmentPicker'
import { getTranslation } from '../util/getTranslation'
import { useAuth } from './AuthContext'
import { FormTextField } from './FormTextField'

type TProps = {
  onSuccess(): void
}

GoPayCredentialsForm.segment = segment('gopay')

export function GoPayCredentialsForm({ onSuccess }: TProps) {
  const config = useAuth()
  const {
    segmentCredentials,
    segmentConfig: { testCredentials },
  } = config.account as GoPay.TSegmentAccount
  const form = useForm<GoPay.TSegmentCredentials>({
    reValidateMode: 'onBlur',
  })

  const [testMode, setTestMode] = React.useState(true)
  const [status, setStatus] = React.useState<'init' | 'failed' | 'done'>('init')

  React.useEffect(() => {
    if (testMode) {
      form.reset({
        clientId: testCredentials?.clientId,
        clientSecret: testCredentials?.clientSecret,
        goId: testCredentials?.goId,
      })
    } else {
      form.reset({
        clientId: segmentCredentials.clientId,
        clientSecret: segmentCredentials.clientSecret,
        goId: segmentCredentials.goId,
      })
    }
  }, [testMode, form, segmentCredentials, testCredentials])

  const onSubmit = form.handleSubmit(async (values) => {
    setStatus('init')
    const api = createApi()
    const { success } = await api.verifyCredentials(values, testMode)
    if (success) {
      setStatus('done')
      if (testMode) {
        setTestMode(false)
      } else {
        onSuccess()
      }
    } else {
      setStatus('failed')
    }
  })

  const onSynchCredentials = async () => {
    const api = createApi()
    api.synchCredentials().then(({account}) => {
      if (account) {
        config.account.segmentCredentials = account.segmentCredentials;
        config.account.segmentConfig.testCredentials = account.segmentConfig.testCredentials;
        form.reset(testMode ? account.segmentConfig.testCredentials : account.segmentCredentials);
        console.log('Updated account:', config.account);
      }
    })
  }

  return (
    <Stack vertical>
      <Card sectioned title={getTranslation('gopay.sync_credentials_title')}>
        <Stack distribution="equalSpacing">
          <TextContainer>
            {getTranslation('gopay.sync_credentials_description')}
          </TextContainer>
          <Button primary submit onClick={onSynchCredentials}>
            {getTranslation('common.synchronize')}
          </Button>
        </Stack>
      </Card>
      <Card sectioned title={getTranslation('gopay.credentials_title')}>
        <Stack vertical>
          <TextContainer>
            <p>
              {getTranslation('gopay.credentials_description')}
            </p>
            <p>
              <TextStyle variation="strong">
                {testMode
                  ? getTranslation('gopay.test_credentials_instruction')
                  : getTranslation('gopay.production_credentials_instruction')}
              </TextStyle>
            </p>
          </TextContainer>
          <FormProvider {...form}>
            <Form onSubmit={onSubmit}>
              <FormLayout>
                              <FormTextField name="clientId" label={getTranslation('fields.client_id')} />
              <FormTextField name="clientSecret" label={getTranslation('fields.client_secret')} />
              <FormTextField name="goId" label={getTranslation('fields.go_id')} validationPattern={/^[0-9]{10}$/} />
                <Stack distribution="trailing">
                  <Button primary submit disabled={form.formState.isSubmitting}>
                    {testMode
                      ? getTranslation('gopay.save_test_credentials')
                      : getTranslation('gopay.save_production_credentials')}
                  </Button>
                </Stack>
                {status === 'failed' && (
                  <Banner status="critical">
                    <Heading>
                      {getTranslation('gopay.verification_failed')}
                    </Heading>
                    <p>
                      {getTranslation('gopay.verification_failed_description')}
                    </p>
                  </Banner>
                )}
              </FormLayout>
            </Form>
          </FormProvider>
          <TextContainer>
            <Heading>{getTranslation('gopay.no_account_title')}</Heading>
            <p>
              {getTranslation('gopay.no_account_description')}
            </p>
          </TextContainer>
        </Stack>
      </Card>
    </Stack>
  )
}
